/**
 * @fileoverview Horizontal scrolling metrics row displaying 10 conversion funnel KPIs
 * for Personal Quick Reaction ("Reacción Rápida Personal" - Tile ID 45).
 * Touch-optimized for mobile and tablet operators with fluent overflow-x scrolling.
 * 
 * @module components/Admin/Overlays/QuickReactionPersonal/QuickReactionPersonalMetricsRow
 */

import React from 'react';

/**
 * Metric Card Item Component.
 * 
 * @function MetricCard
 * @param {Object} props
 * @param {string} props.icon - Bootstrap icon class name.
 * @param {string} props.title - Descriptive title of the KPI.
 * @param {string|number} props.value - Formatted value or count.
 * @param {string} [props.unit] - Optional unit of measurement (e.g. 'ms', '%').
 * @param {string} [props.colorScheme='primary'] - Accent color scheme.
 * @param {string} [props.subtext] - Supplementary explanation or conversion note.
 * @returns {React.ReactElement} Metric card markup.
 */
const MetricCard = ({
    icon,
    title,
    value,
    unit = '',
    colorScheme = 'primary',
    subtext = ''
}) => {
    return (
        <div className={`QuickReactionPersonalBoard__MetricCard QuickReactionPersonalBoard__MetricCard--${colorScheme} flex-shrink-0 card border-0 shadow-sm rounded-3 p-3`}>
            <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="QuickReactionPersonalBoard__MetricTitle text-muted text-uppercase fw-bold small">
                    {title}
                </span>
                <div className={`QuickReactionPersonalBoard__MetricIconBox QuickReactionPersonalBoard__MetricIconBox--${colorScheme} rounded-circle d-flex align-items-center justify-content-center`}>
                    <i className={`bi ${icon} fs-5`}></i>
                </div>
            </div>
            <div className="d-flex align-items-baseline gap-1">
                <span className="QuickReactionPersonalBoard__MetricValue fw-bold text-dark fs-3">
                    {value != null ? value : 0}
                </span>
                {unit && <span className="text-secondary fw-semibold small">{unit}</span>}
            </div>
            {subtext && (
                <div className="QuickReactionPersonalBoard__MetricSubtext text-muted small mt-1 text-truncate" title={subtext}>
                    {subtext}
                </div>
            )}
        </div>
    );
};

/**
 * 10-KPI Telemetry Funnel Row Component.
 * 
 * @function QuickReactionPersonalMetricsRow
 * @param {Object} props
 * @param {Object} props.metrics - Aggregated funnel metrics payload from backend.
 * @param {number} [props.totalParticipants=0] - Total participants count.
 * @returns {React.ReactElement} Metrics row markup.
 */
export const QuickReactionPersonalMetricsRow = ({ metrics = {}, totalParticipants = 0 }) => {
    const qrScan = metrics.qrScanCount || 0;
    const pageView = metrics.pageViewCount || 0;
    const formStart = metrics.formStartCount || 0;
    const formComplete = metrics.formCompleteCount || 0;
    const gameStart = metrics.gameStartCount || 0;
    const triggerFired = metrics.triggerFiredCount || 0;
    const gameComplete = metrics.gameCompleteCount || 0;

    const recordTime = metrics.recordReactionTimeMs != null ? `${metrics.recordReactionTimeMs}` : '---';
    const avgTime = metrics.averageReactionTimeMs != null ? `${metrics.averageReactionTimeMs}` : '---';
    const successRate = metrics.successRatePercentage != null ? `${metrics.successRatePercentage}` : '0';

    const baseFunnel = Math.max(qrScan, pageView, 1);
    const pageConv = qrScan > 0 ? `${((pageView / qrScan) * 100).toFixed(0)}%` : '100%';
    const regConv = baseFunnel > 0 ? `${((formComplete / baseFunnel) * 100).toFixed(0)}%` : '0%';

    return (
        <div className="QuickReactionPersonalBoard__MetricsWrapper mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="fw-bold text-secondary text-uppercase mb-0 small d-flex align-items-center gap-2">
                    <i className="bi bi-bar-chart-fill text-danger"></i>
                    Embudo de Conversión y Telemetría en Vivo (10 KPIs)
                </h6>
                <span className="badge bg-light text-secondary border small">
                    <i className="bi bi-people me-1"></i> {totalParticipants} Registros
                </span>
            </div>

            <div className="QuickReactionPersonalBoard__MetricsScroll d-flex flex-nowrap gap-3 overflow-auto pb-2">
                {/* 1. Escaneos QR */}
                <MetricCard
                    icon="bi-qr-code-scan"
                    title="1. Escaneos QR"
                    value={qrScan}
                    colorScheme="info"
                    subtext="Impactos en Broadcast / Cancha"
                />

                {/* 2. Vistas Web */}
                <MetricCard
                    icon="bi-phone"
                    title="2. Vistas Móvil"
                    value={pageView}
                    colorScheme="primary"
                    subtext={`Conversión vs QR: ${pageConv}`}
                />

                {/* 3. Formulario Iniciado */}
                <MetricCard
                    icon="bi-input-cursor-text"
                    title="3. Inicios Form"
                    value={formStart}
                    colorScheme="secondary"
                    subtext="Interacción con inputs"
                />

                {/* 4. Formulario Completo */}
                <MetricCard
                    icon="bi-check-circle-fill"
                    title="4. Completados"
                    value={formComplete}
                    colorScheme="success"
                    subtext={`Efectividad global: ${regConv}`}
                />

                {/* 5. Partidas Iniciadas */}
                <MetricCard
                    icon="bi-play-fill"
                    title="5. Partidas"
                    value={gameStart}
                    colorScheme="warning"
                    subtext="Conteo 3-2-1 iniciado"
                />

                {/* 6. Triggers Señal */}
                <MetricCard
                    icon="bi-bell-fill"
                    title="6. Triggers"
                    value={triggerFired}
                    colorScheme="warning"
                    subtext="Giro a Sprite Frente"
                />

                {/* 7. Partidas Finalizadas */}
                <MetricCard
                    icon="bi-flag-fill"
                    title="7. Finalizadas"
                    value={gameComplete}
                    colorScheme="success"
                    subtext="Tiempos válidos y faltas"
                />

                {/* 8. Récord Sala */}
                <MetricCard
                    icon="bi-lightning-charge-fill"
                    title="8. Récord Sala"
                    value={recordTime}
                    unit="ms"
                    colorScheme="danger"
                    subtext="⚡ Mejor tiempo histórico"
                />

                {/* 9. Promedio Sala */}
                <MetricCard
                    icon="bi-speedometer2"
                    title="9. Promedio"
                    value={avgTime}
                    unit="ms"
                    colorScheme="primary"
                    subtext="Velocidad media de afición"
                />

                {/* 10. Tasa de Éxito */}
                <MetricCard
                    icon="bi-award-fill"
                    title="10. Tasa Éxito"
                    value={successRate}
                    unit="%"
                    colorScheme="info"
                    subtext="Partidas sin abandono"
                />
            </div>
        </div>
    );
};
